import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { LOGO_URL } from "@/lib/constants";
import { VIP_RECIPIENTS, GENERIC_INVITE, VIP_STORAGE_KEY } from "@/lib/vipRecipients";

const revealLine = (i, delay = 0.35) => ({
    initial: { y: "110%" },
    animate: { y: "0%" },
    transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1], delay: delay + i * 0.14 }
});

export default function VIPPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [ready, setReady] = useState(false);

    const recipient = slug && VIP_RECIPIENTS[slug] ? VIP_RECIPIENTS[slug] : GENERIC_INVITE;
    const isPersonal = slug && VIP_RECIPIENTS[slug];

    // Skip the gate if this browser already entered — go straight to the site.
    useEffect(() => {
        try {
            if (localStorage.getItem(VIP_STORAGE_KEY) === "1") {
                navigate("/", { replace: true });
                return;
            }
        } catch {}
        setReady(true);
    }, [navigate]);

    const enter = () => {
        try { localStorage.setItem(VIP_STORAGE_KEY, "1"); } catch {}
        navigate("/");
    };

    if (!ready) return null;

    const dateLine = new Date().toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric"
    }).toUpperCase();

    return (
        <div
            className="fixed inset-0 z-[100] bg-paper text-navy overflow-hidden flex flex-col"
            data-testid="vip-page"
        >
            {/* Subtle grid backdrop */}
            <div className="absolute inset-0 bg-grid opacity-70 pointer-events-none" aria-hidden="true"></div>
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at 50% 30%, rgba(200,155,42,0.10) 0%, rgba(245,241,234,0) 60%)"
                }}
                aria-hidden="true"
            />

            {/* Top bar */}
            <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative flex items-center justify-between px-8 lg:px-16 pt-8 lg:pt-10"
            >
                <img src={LOGO_URL} alt="Carbonis" className="h-14 w-auto" />
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-navy/50">
                    Private · {dateLine}
                </div>
            </motion.div>

            {/* Center content */}
            <div className="relative flex-1 flex items-center px-8 lg:px-16 py-8">
                <div className="max-w-[1400px] mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-9">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.15 }}
                            className="flex items-center gap-4 mb-8"
                        >
                            <div className="w-12 h-px bg-gold"></div>
                            <span className="font-mono text-[11px] tracking-[0.32em] uppercase text-gold">
                                {isPersonal ? "A Personal Invitation" : "Private Preview"}
                            </span>
                        </motion.div>

                        <h1
                            className="font-serif leading-[0.95] tracking-[-0.03em] text-navy text-[52px] sm:text-[76px] md:text-[92px] lg:text-[104px] xl:text-[120px]"
                            data-testid="vip-headline"
                        >
                            <span className="line-mask">
                                <motion.span {...revealLine(0)} className="block">
                                    You are
                                </motion.span>
                            </span>
                            <span className="line-mask">
                                <motion.span {...revealLine(1)} className="block">
                                    <em className="not-italic font-light text-gold">invited.</em>
                                </motion.span>
                            </span>
                        </h1>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 1.05 }}
                            className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8"
                        >
                            <div className="md:col-span-7 max-w-[600px]">
                                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-navy/40 mb-3">
                                    Prepared for
                                </div>
                                <div
                                    className="font-serif text-3xl md:text-4xl text-navy leading-tight"
                                    data-testid="vip-recipient-name"
                                >
                                    {recipient.name}
                                </div>
                                {recipient.role && (
                                    <div className="mt-2 text-navy/60 text-base">
                                        {recipient.role}
                                    </div>
                                )}
                            </div>
                            <div className="md:col-span-5 flex md:justify-end md:items-end">
                                <button
                                    onClick={enter}
                                    className="btn-institution !py-4 !px-8"
                                    data-testid="vip-enter-btn"
                                >
                                    Enter Carbonis <ArrowRight size={16} strokeWidth={1.5} />
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Bottom band */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.4 }}
                className="relative px-8 lg:px-16 pb-8 lg:pb-10"
            >
                <div className="hairline mb-6"></div>
                <div className="flex flex-col md:flex-row justify-between gap-4 text-[10px] font-mono uppercase tracking-[0.22em] text-navy/45">
                    <span>
                        A private preview of Carbonis — Climate Risk Intelligence for banks, regulators, and MSMEs in emerging markets.
                    </span>
                    <span className="hidden md:inline">
                        © Carbonis International · An UP/OR/OUT Company
                    </span>
                </div>
            </motion.div>
        </div>
    );
}
